import React, { useEffect } from 'react';
import { tokens } from './listPanelStyles';

/**
 * ConfirmModal — styled confirmation dialog for the Explorer.
 *
 * Replaces every `window.confirm` / `alert` / `prompt` in survey-explorer
 * (native dialogs are banned project-wide). The caller owns the open/closed
 * state; render it always and toggle `open`. Colors come from the shared
 * design tokens — no hex literals here.
 *
 * @param {boolean} open          When false, nothing renders.
 * @param {string}  title         Dialog title. Defaults to "Confirm".
 * @param {string|React.ReactNode} message Body content (string or node).
 * @param {string}  confirmLabel  Confirm button label. Defaults to "Confirm".
 * @param {string}  cancelLabel   Cancel button label. Defaults to "Cancel".
 * @param {boolean} danger        When true, the confirm button is red.
 * @param {boolean} busy          When true, both buttons are disabled and the
 *                                confirm button reads "Working…".
 * @param {Function} onConfirm    Called when the confirm button is clicked.
 * @param {Function} onCancel     Called on Cancel, Escape, or backdrop click.
 */
export default function ConfirmModal({
    open,
    title = 'Confirm',
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
    busy = false,
    onConfirm,
    onCancel,
}) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, busy, onCancel]);

    if (!open) return null;

    return (
        <div style={styles.backdrop} onClick={() => { if (!busy) onCancel?.(); }}>
            <div style={styles.card} onClick={(e) => e.stopPropagation()}>
                <h3 style={styles.title}>{title}</h3>
                <div style={styles.message}>{message}</div>
                <div style={styles.actions}>
                    <button
                        type="button"
                        style={styles.cancelBtn}
                        onClick={() => onCancel?.()}
                        disabled={busy}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        autoFocus
                        style={{ ...styles.confirmBtn, background: danger ? tokens.error : tokens.primary }}
                        onClick={() => onConfirm?.()}
                        disabled={busy}
                    >
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

const styles = {
    backdrop: {
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: tokens.spaceBase,
    },
    card: {
        background: tokens.panelBg,
        borderRadius: tokens.radiusLg,
        padding: tokens.spaceXl,
        maxWidth: 460,
        width: '100%',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        fontFamily: tokens.fontFamily,
    },
    title: {
        margin: `0 0 ${tokens.spaceMd}px`,
        fontSize: tokens.fontSizeLg,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.textPrimary,
    },
    message: {
        margin: `0 0 ${tokens.spaceXl}px`,
        fontSize: tokens.fontSizeMd,
        color: tokens.textSecondary,
        lineHeight: 1.5,
    },
    actions: {
        display: 'flex', gap: tokens.spaceSm, justifyContent: 'flex-end',
    },
    cancelBtn: {
        padding: '6px 16px',
        border: `1px solid ${tokens.borderMedium}`,
        borderRadius: tokens.radiusSm,
        background: tokens.panelBg,
        color: tokens.textPrimary,
        cursor: 'pointer',
        fontSize: tokens.fontSizeSm,
    },
    confirmBtn: {
        padding: '6px 16px',
        border: 'none',
        borderRadius: tokens.radiusSm,
        color: tokens.textInverse,
        cursor: 'pointer',
        fontSize: tokens.fontSizeSm,
        fontWeight: tokens.fontWeightSemibold,
    },
};
